/**
 * Navigation and page/line geometry — §3.7, §11.3.
 *
 * Everything here is a pure function of its arguments. The line maths is a closed
 * form and needs no index, which is why meta.json does not ship per-page line counts.
 */
import { TOTAL_LINES, TOTAL_PAGES } from './constants'
import type { ChapterMeta, Meta, PageMeta, VerseRef } from './types'

// ---------------------------------------------------------------------------
// Verse keys
// ---------------------------------------------------------------------------

export function parseVerseKey(key: string): VerseRef | null {
  const at = key.indexOf(':')
  if (at <= 0) return null
  const surah = Number(key.slice(0, at))
  const ayah = Number(key.slice(at + 1))
  if (!Number.isInteger(surah) || !Number.isInteger(ayah)) return null
  if (surah < 1 || surah > 114 || ayah < 1) return null
  return { surah, ayah }
}

export const verseKey = (surah: number, ayah: number): string => `${surah}:${ayah}`

/** Ordering by surah then ayah — never lexicographic, or 2:10 sorts before 2:2. */
export function compareVerseKeys(a: string, b: string): number {
  const x = parseVerseKey(a)
  const y = parseVerseKey(b)
  if (!x || !y) return a.localeCompare(b)
  return x.surah - y.surah || x.ayah - y.ayah
}

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

export const clampPage = (page: number): number =>
  Math.min(TOTAL_PAGES, Math.max(1, Math.floor(page) || 1))

/**
 * Which page a verse is on — a range check against meta.pages[].first/last, not a
 * scan of 604 page files. That is what those two fields are for (§3.2).
 */
export function pageOfVerse(pages: readonly PageMeta[], key: string): number | null {
  const ref = parseVerseKey(key)
  if (!ref) return null
  for (const page of pages) {
    const first = parseVerseKey(page.first)
    const last = parseVerseKey(page.last)
    if (!first || !last) continue
    const afterStart =
      ref.surah > first.surah || (ref.surah === first.surah && ref.ayah >= first.ayah)
    const beforeEnd = ref.surah < last.surah || (ref.surah === last.surah && ref.ayah <= last.ayah)
    if (afterStart && beforeEnd) return page.page
  }
  return null
}

export function pageMetaFor(meta: Meta, page: number): PageMeta | null {
  return meta.pages.find((p) => p.page === page) ?? null
}

export const juzOfPage = (meta: Meta, page: number): number => pageMetaFor(meta, page)?.juz ?? 1

export function firstPageOfJuz(meta: Meta, juz: number): number {
  return clampPage(meta.juzStartPage[String(juz)] ?? 1)
}

/** Inclusive page range of a juz. Juz 30 runs to the end of the mushaf. */
export function juzPageRange(meta: Meta, juz: number): [number, number] {
  const from = firstPageOfJuz(meta, juz)
  const to = juz >= 30 ? TOTAL_PAGES : firstPageOfJuz(meta, juz + 1) - 1
  return [from, Math.max(from, to)]
}

export function firstPageOfSurah(meta: Meta, surah: number): number {
  const chapter = meta.chapters.find((c) => c.id === surah)
  return chapter ? clampPage(chapter.pages[0]) : 1
}

// ---------------------------------------------------------------------------
// Line geometry (§3.7)
// ---------------------------------------------------------------------------

/** 602 pages carry 15 lines; pages 1 and 2 carry 8. */
export const linesOnPage = (page: number): number => (page <= 2 ? 8 : 15)

/**
 * Lines before the given page. Closed form, so no index is needed:
 * linesBefore(605) === 9046 === TOTAL_LINES.
 */
export function linesBefore(page: number): number {
  if (page <= 1) return 0
  if (page === 2) return 8
  return 16 + (page - 3) * 15
}

export const toAbsoluteLine = (pos: { page: number; line: number }): number =>
  linesBefore(pos.page) + pos.line - 1

export function fromAbsoluteLine(absolute: number): { page: number; line: number } {
  const n = Math.min(TOTAL_LINES - 1, Math.max(0, Math.floor(absolute)))
  if (n < 8) return { page: 1, line: n + 1 }
  if (n < 16) return { page: 2, line: n - 8 + 1 }
  const after = n - 16
  return { page: 3 + Math.floor(after / 15), line: (after % 15) + 1 }
}

// ---------------------------------------------------------------------------
// Ayah keys on a page
// ---------------------------------------------------------------------------

/**
 * The ayah keys a page contains, in reading order, derived from meta alone — so the
 * queue for a page can be built before that page's JSON has arrived.
 */
export function ayahKeysOnPage(meta: Meta, page: number): string[] {
  const pm = pageMetaFor(meta, page)
  if (!pm) return []
  const first = parseVerseKey(pm.first)
  const last = parseVerseKey(pm.last)
  if (!first || !last) return []

  const keys: string[] = []
  for (let surah = first.surah; surah <= last.surah; surah += 1) {
    const chapter = meta.chapters.find((c) => c.id === surah)
    if (!chapter) continue
    const from = surah === first.surah ? first.ayah : 1
    const to = surah === last.surah ? last.ayah : chapter.versesCount
    for (let ayah = from; ayah <= to; ayah += 1) keys.push(verseKey(surah, ayah))
  }
  return keys
}

/**
 * The chapters a page carries, as FACTS — not as a formatted name.
 *
 * This replaced `surahNamesOnPage`, which returned `nameArabic` already picked. That
 * was fine in an Arabic-rooted app and wrong here: the page header wants the Arabic
 * name under `lang="ar"`, the top bar wants "18 · Al-Kahf — The Cave", and the search
 * panel wants to match on either. One formatted string cannot serve three callers, and
 * §4.2 says this package returns facts and `content/` says them (I3).
 */
export function chaptersOnPage(meta: Meta, page: number): ChapterMeta[] {
  const pm = pageMetaFor(meta, page)
  if (!pm) return []
  return pm.surahs
    .map((id) => meta.chapters.find((c) => c.id === id))
    .filter((c): c is ChapterMeta => c !== undefined)
}
